'use client';

import dayjs from 'dayjs';
import useSWR from 'swr';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { DashboardContent } from 'src/layouts/dashboard';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';
import { useAppContext } from '@/src/context/app';

import axiosInstance from 'src/lib/axios';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { useGetTransaktioner } from 'src/actions/bank';
import { hämtaBankkonton } from 'src/sections/bank/BankService';
import type { IBankkonto, IBanktransaktion } from 'src/types/nya/ekonomi/bank/BankTyper';
import { EmailIntegrationService, InkommandeEmail } from 'src/sections/email/EmailIntegrationService';
import { hämtaEllerSkapaBokslutProcess, hämtaÅrsredovisningsunderlag } from 'src/sections/bokforing/bokslut/BokslutProcessService';
import { HämtaMatchningarAvLöner } from 'src/sections/loner/LoneService';
import {
    hämtaAllaKundfakturorInklBokförda,
    type IKundfakturaFörslag,
} from 'src/sections/bokforing/affarshandelser/skapa/kundfaktura/KundfakturaService';
import { HämtaMatchningar } from 'src/sections/inkop/InkopService';
import { hämtaSkattekontoutdrag } from 'src/sections/skatt/SkatteService';
import { hämtaAffärshändelse } from 'src/sections/bokforing/BokforingsService';
import { useGetAktieutdelningFörslag, type AktieutdelningFörslagDto } from 'src/actions/aktieutdelning';
import { formatMomsperiod, SVENSKA_MÅNADER } from 'src/sections/moms/momsPeriod';
import type { LöneResponse, IMatchningAvLön } from 'src/types/nya/ekonomi/loner/LoneTyper';
import type { InköpParsningResponse, IInköp } from 'src/types/nya/ekonomi/inkop/InkopTyper';
import type { SkattekontoutdragRespons } from 'src/types/nya/ekonomi/skatt/SkatteTyper';
import type { IBokslutProcess } from 'src/types/nya/ekonomi/bokforing/bokslutTyper';
import type { IOrganisation, IRäkenskapsår } from 'src/types/nya/common';

import DynamicDashboard, { DashboardWidget } from './dynamic-dashboard';
import WelcomeBanner from '../components/welcome-banner';
import SummaryPills, { SummaryPill } from '../components/summary-pills';
import DomainCard from '../components/domain-card';

// ----------------------------------------------------------------------
// Formatters
// ----------------------------------------------------------------------

const formatKr = (n: number) =>
    `${n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;

const formatSignedKr = (n: number) => {
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    return `${sign}${Math.abs(n).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
};

const formatRelativeSync = (iso?: string) => {
    if (!iso) return 'Aldrig';
    const then = dayjs(iso);
    if (!then.isValid()) return 'Aldrig';
    const now = dayjs();
    const diffMin = now.diff(then, 'minute');
    if (diffMin < 1) return 'Just nu';
    if (diffMin < 60) return `${diffMin} min sedan`;
    const diffH = now.diff(then, 'hour');
    if (diffH < 24) return `${diffH} tim sedan`;
    const diffD = now.diff(then, 'day');
    if (diffD < 7) return `${diffD} d sedan`;
    return then.format('D MMM');
};

const formatRelativeReceived = (iso?: string) => {
    if (!iso) return '';
    const then = dayjs(iso);
    if (!then.isValid()) return '';
    const now = dayjs();
    if (then.isSame(now, 'day')) return `Idag ${then.format('HH:mm')}`;
    if (then.isSame(now.subtract(1, 'day'), 'day')) return `Igår ${then.format('HH:mm')}`;
    return then.format('D MMM');
};

const toDayjsSafe = (d: any) => {
    if (!d) return null;
    if (typeof d?.format === 'function') return d;
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed : null;
};

/** Formaterar ett datum som "år månad", t.ex. "2026 maj". */
const formatÅrMånad = (d: any) => {
    const day = toDayjsSafe(d);
    return day ? `${day.year()} ${SVENSKA_MÅNADER[day.month()]}` : undefined;
};

/**
 * Plockar ut det löpande saldot (sista kolumnen) ur en rådata-rad från skattekontoutdraget,
 * t.ex. `"2026-07-14";"Inbetalning...";"30 000";"19 413"` → 19413.
 */
const parseSkattekontoSaldo = (rådata?: string): number | undefined => {
    if (!rådata) return undefined;
    const fält = rådata.match(/"([^"]*)"/g);
    if (!fält || fält.length === 0) return undefined;
    const sista = fält[fält.length - 1].replace(/"/g, '');
    const n = Number(sista.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) && sista.trim() !== '' ? n : undefined;
};

/**
 * Letar upp räkenskapsårslistan på organisationen oavsett exakt nyckelstavning
 * (backenden serialiserar den som "räkenskapsår", typen har historiskt haft "rakenskapsar").
 */
function hittaRäkenskapsårLista(organisation: IOrganisation | undefined): IRäkenskapsår[] | undefined {
    if (!organisation || typeof organisation !== 'object') return undefined;
    for (const [nyckel, värde] of Object.entries(organisation as Record<string, unknown>)) {
        if (Array.isArray(värde) && värde.length > 0 && /r[aä]kensk/i.test(nyckel)) {
            return värde as IRäkenskapsår[];
        }
    }
    return undefined;
}

/** Senast avslutade kalenderår (föregående år) som räkenskapsår — sista utväg när organisationen saknar lista. */
function senastAvslutadeKalenderårSomRäkenskapsår(): IRäkenskapsår {
    const föregåendeÅr = dayjs().subtract(1, 'year');
    return { förstaDag: föregåendeÅr.startOf('year'), sistaDag: föregåendeÅr.endOf('year') } as IRäkenskapsår;
}

/**
 * Härleder ett räkenskapsår för bokslut: det senast avslutade räkenskapsåret (störst sistaDag
 * som ligger före idag). Finns inget avslutat tas det senaste. Faller tillbaka på föregående
 * kalenderår om organisationen saknar räkenskapsårslista.
 */
function härledRäkenskapsår(organisation: IOrganisation | undefined): IRäkenskapsår {
    const list = hittaRäkenskapsårLista(organisation);
    if (!list || list.length === 0) return senastAvslutadeKalenderårSomRäkenskapsår();
    const idag = dayjs();
    const efterSistaDag = (a: IRäkenskapsår, b: IRäkenskapsår) =>
        (toDayjsSafe(b.sistaDag)?.valueOf() ?? 0) - (toDayjsSafe(a.sistaDag)?.valueOf() ?? 0);

    const avslutade = list
        .filter((r) => {
            const slut = toDayjsSafe(r.sistaDag);
            return slut && slut.isBefore(idag, 'day');
        })
        .sort(efterSistaDag);
    if (avslutade.length > 0) return avslutade[0];

    // Inget avslutat räkenskapsår ännu — ta det senaste.
    return [...list].sort(efterSistaDag)[0];
}

// ----------------------------------------------------------------------
// SWR hooks wrapping the promise-based services
// ----------------------------------------------------------------------

const swrOptions = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
};

function useBankkonton(orgId: string | undefined) {
    const swr = useSWR<IBankkonto[]>(
        orgId ? ['bankkonton', orgId] : null,
        () => hämtaBankkonton(orgId!),
        swrOptions
    );
    return {
        bankkonton: swr.data ?? [],
        bankkontonLoading: swr.isLoading,
    };
}

function useInkommandeEmail(orgId: string | undefined) {
    const swr = useSWR<InkommandeEmail[]>(
        orgId ? ['inkommande-email', orgId] : null,
        () => EmailIntegrationService.hämtaMottagnaEmail(orgId!),
        swrOptions
    );
    return {
        email: swr.data ?? [],
        emailLoading: swr.isLoading,
        emailError: swr.error,
    };
}

function useBokslutProcess(organisation: IOrganisation | undefined, rakenskapsar: IRäkenskapsår | undefined) {
    const orgId = organisation?.id;
    const start = rakenskapsar?.förstaDag;
    const slut = rakenskapsar?.sistaDag;
    const startKey = start ? String((start as any).format?.('YYYY-MM-DD') ?? start) : '';
    const slutKey = slut ? String((slut as any).format?.('YYYY-MM-DD') ?? slut) : '';

    const swr = useSWR<IBokslutProcess>(
        orgId && rakenskapsar ? ['bokslut-process', orgId, startKey, slutKey] : null,
        () => hämtaEllerSkapaBokslutProcess(orgId!, rakenskapsar!),
        swrOptions
    );
    return {
        process: swr.data,
        processLoading: swr.isLoading,
        processError: swr.error,
    };
}

function useLöner(organisation: IOrganisation | undefined, rakenskapsar: IRäkenskapsår | undefined) {
    const orgId = organisation?.id;
    const start = rakenskapsar?.förstaDag;
    const startKey = start ? String((start as any).format?.('YYYY-MM-DD') ?? start) : '';

    const swr = useSWR<LöneResponse>(
        orgId ? ['loner-matchningar', orgId, startKey] : null,
        () => HämtaMatchningarAvLöner(organisation ?? null, rakenskapsar ?? null),
        swrOptions
    );
    return {
        matchningar: swr.data?.matchningarAvLöner ?? [],
        lönerLoading: swr.isLoading,
    };
}

function useKundfakturor(orgId: string | undefined) {
    const swr = useSWR<IKundfakturaFörslag[]>(
        orgId ? ['kundfakturor-alla', orgId] : null,
        () => hämtaAllaKundfakturorInklBokförda(orgId!),
        swrOptions
    );
    return {
        kundfakturor: swr.data ?? [],
        kundfakturorLoading: swr.isLoading,
    };
}

interface IKundfakturaDetalj {
    period?: any;
    exklMoms: number;
    moms: number;
    kund?: string;
}

/** Härleder fakturaperiod samt belopp exkl. moms och moms ur affärshändelsens konteringar. */
function beräknaKundfakturaDetalj(ah: any): IKundfakturaDetalj {
    const total = ah?.totaltBelopp ?? 0;
    const konteringar: any[] = (ah?.verifikationer ?? []).flatMap((v: any) => v?.konteringar ?? []);
    // Utgående moms bokförs på konto 26xx (t.ex. 2611). Summan av dessa är momsbeloppet.
    const moms = konteringar
        .filter((k) => {
            const n = k?.bokföringskonto?.kontonummer;
            return typeof n === 'number' && n >= 2600 && n < 2700;
        })
        .reduce((s, k) => s + Math.abs(k?.belopp ?? 0), 0);
    return { period: ah?.inträffade, exklMoms: total - moms, moms, kund: ah?.motpart?.namn };
}

/**
 * Hämtar detaljer (period, belopp exkl. moms, moms) för ett fåtal kundfakturor genom att
 * slå upp deras affärshändelser. Håll listan kort — ett anrop per affärshändelse.
 */
function useKundfakturaDetaljer(orgId: string | undefined, affärshändelseIds: string[]) {
    const idNyckel = affärshändelseIds.join(',');
    const swr = useSWR<Record<string, IKundfakturaDetalj>>(
        orgId && idNyckel ? ['kundfaktura-detaljer', orgId, idNyckel] : null,
        async () => {
            const poster = await Promise.all(
                affärshändelseIds.map(async (id) => {
                    const respons: any = await hämtaAffärshändelse(orgId!, id);
                    const ah = respons?.affärshändelse ?? respons;
                    return [id, beräknaKundfakturaDetalj(ah)] as const;
                })
            );
            return Object.fromEntries(poster);
        },
        swrOptions
    );
    return swr.data ?? {};
}

function useInköp(orgId: string | undefined) {
    const swr = useSWR<InköpParsningResponse>(
        orgId ? ['inkop-matchningar', orgId] : null,
        () => HämtaMatchningar(orgId!),
        swrOptions
    );
    return {
        inköpRespons: swr.data,
        inköpLoading: swr.isLoading,
    };
}

function useSkattekontoutdrag(orgId: string | undefined) {
    const swr = useSWR<SkattekontoutdragRespons>(
        orgId ? ['skattekontoutdrag', orgId] : null,
        () => hämtaSkattekontoutdrag(orgId!),
        swrOptions
    );
    return {
        skattekontoutdrag: swr.data,
        skattekontoutdragLoading: swr.isLoading,
    };
}

interface IMomsdeklaration {
    id?: string;
    period?: { förstaDag?: string; sistaDag?: string };
    sistaDagIPerioden?: string;
    inlämnadPå?: string | null;
}

function useMomsdeklarationer(orgId: string | undefined) {
    const swr = useSWR<IMomsdeklaration[]>(
        orgId ? ['momsdeklarationer', orgId] : null,
        () =>
            axiosInstance
                .get<IMomsdeklaration[]>(`/api/momsdeklaration/${orgId}`)
                .then((r) => r.data ?? []),
        swrOptions
    );
    return {
        momsdeklarationer: swr.data ?? [],
        momsLoading: swr.isLoading,
    };
}

/** Hämtar beslutad aktieutdelning (från årsredovisningsunderlaget) för räkenskapsåret. */
function useBeslutadAktieutdelning(orgId: string | undefined, start?: string, slut?: string) {
    const swr = useSWR<number | undefined>(
        orgId && start && slut ? ['arsredovisning-underlag-utdelning', orgId, start, slut] : null,
        () => hämtaÅrsredovisningsunderlag(orgId!, start!, slut!).then((u) => u?.aktieutdelning),
        swrOptions
    );
    return swr.data;
}

// ----------------------------------------------------------------------

export function AppDashboardGamla () {
    const { anvandare } = useAuthContext();
    // `organisation` är föreningen och är undefined för Privatperson — det är
    // den alla hämtningar nedan gatear på. Bannern visar däremot valet som det
    // är, så att Privatperson syns med sitt namn.
    const { organisation, valdOrganisation, ärPrivatperson, aktuelltRäkenskapsår } = useAppContext();

    const orgType = valdOrganisation?.organisationsTyp;

    // All hooks run on every render. They short-circuit internally when the
    // required ids are missing.
    const { bankkonton, bankkontonLoading } = useBankkonton(organisation?.id);
    const { transaktioner: banktransaktioner, transaktionerLoading: banktransaktionerLoading } =
        useGetTransaktioner(false);
    const { email, emailLoading } = useInkommandeEmail(organisation?.id);

    // Fall tillbaka på organisationens räkenskapsår när inget är valt i appkontexten,
    // så att t.ex. Bokslut- och Lönekorten kan visa data utan ett manuellt val.
    const härlettRäkenskapsår = aktuelltRäkenskapsår ?? härledRäkenskapsår(organisation);

    const { process: bokslutProcess, processLoading: bokslutLoading } = useBokslutProcess(
        organisation,
        härlettRäkenskapsår
    );
    const { matchningar: löneMatchningar, lönerLoading } = useLöner(organisation, härlettRäkenskapsår);
    const { kundfakturor, kundfakturorLoading } = useKundfakturor(organisation?.id);
    const senasteKundfakturaIds = [...kundfakturor]
        .sort((a, b) => dayjs(b.uppladdadVid).valueOf() - dayjs(a.uppladdadVid).valueOf())
        .slice(0, 3)
        .map((f) => f.affärshändelseId)
        .filter((id): id is string => Boolean(id));
    const kundfakturaDetaljer = useKundfakturaDetaljer(organisation?.id, senasteKundfakturaIds);
    const { inköpRespons, inköpLoading } = useInköp(organisation?.id);
    const { skattekontoutdrag, skattekontoutdragLoading } = useSkattekontoutdrag(organisation?.id);
    const { momsdeklarationer, momsLoading } = useMomsdeklarationer(organisation?.id);

    // Vinstdispositionssiffror för bokslutskortet (årets resultat, skatt, efter skatt, utdelning).
    const bokslutStart = toDayjsSafe(härlettRäkenskapsår?.förstaDag)?.format('YYYY-MM-DD');
    const bokslutSlut = toDayjsSafe(härlettRäkenskapsår?.sistaDag)?.format('YYYY-MM-DD');
    const { förslag: aktieutdelningFörslag } = useGetAktieutdelningFörslag(
        organisation?.id,
        bokslutStart,
        bokslutSlut,
        null
    );
    // Beslutad utdelning (från årsredovisningen) — ska visas istället för det beräknade förslaget.
    const beslutadAktieutdelning = useBeslutadAktieutdelning(organisation?.id, bokslutStart, bokslutSlut);

    // ----- Welcome banner shown for every org type -----

    const welcome = (
        <WelcomeBanner
            userName={anvandare?.namn?.förnamn}
            orgName={valdOrganisation?.organisationsNamn}
            orgType={orgType}
            // subtitle={
            //     isAktiebolag
            //         ? 'Här är en snabb överblick av ditt aktiebolag. Välj en domän nedan för att gå djupare.'
            //         : 'Du är inloggad. Välj en organisation eller fortsätt med en annan vy.'
            // }
            actions={[
                {
                    label: 'Till inkorgen',
                    href: paths.dashboard.inkorg,
                    icon: 'solar:inbox-bold',
                },
                {
                    label: 'Lägg till organisation',
                    href: paths.dashboard.organisation.register,
                    variant: 'outlined',
                    icon: 'solar:add-circle-bold',
                },
            ]}
        />
    );

    // ----- Ingen förening vald -----
    //
    // Gäller både Privatperson och ett tomt val. Korten nedan hämtar allt per
    // förening, så utan en sådan finns inget att visa — och inget att hämta:
    // anropen skulle bara besvaras med fel.

    if (!organisation) {
        const widgets: DashboardWidget[] = [
            { id: 'welcome', size: { xs: 12 }, component: welcome },
            {
                id: 'no-org',
                size: { xs: 12 },
                component: ärPrivatperson ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <AlertTitle sx={{ fontWeight: 700 }}>Du är inloggad som privatperson</AlertTitle>
                        Ekonomi, dokument och fastighet hör till en förening. Välj en förening i
                        väljaren uppe till vänster, eller lägg till en ny.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <AlertTitle sx={{ fontWeight: 700 }}>Ingen organisation vald</AlertTitle>
                        Välj eller lägg till en organisation för att se din dashboard.
                    </Alert>
                ),
            },
        ];

        return (
            <DashboardContent maxWidth="xl">
                <DynamicDashboard widgets={widgets} />
            </DashboardContent>
        );
    }

    // ----- Dashboard (alla organisationstyper) -----

    // Real derived values for summary pills.
    const totalBalans = bankkonton.reduce((sum, k) => sum + (k.saldo ?? 0), 0);
    const bokslutKlaraSteg = bokslutProcess?.stegStatus
        ? Object.values(bokslutProcess.stegStatus).filter((s) => s === 'KLAR').length
        : 0;
    const bokslutTotaltSteg = 6;

    const pills: SummaryPill[] = [];
    if (email.length > 0) {
        pills.push({
            label: `${email.length} mail i inkorgen`,
            color: ChipFärger[0],
            icon: 'solar:inbox-bold',
            href: paths.dashboard.inkorg,
        });
    }
    if (bankkonton.length > 0) {
        pills.push({
            label: `${formatKr(totalBalans)} på ${bankkonton.length} ${bankkonton.length === 1 ? 'konto' : 'konton'}`,
            color: ChipFärger[5],
            icon: 'solar:card-bold',
            href: paths.dashboard.ekonomi.bank.root,
        });
    }
    if (bokslutProcess) {
        pills.push({
            label: `Bokslut ${bokslutKlaraSteg}/${bokslutTotaltSteg} steg`,
            color: ChipFärger[7],
            icon: 'solar:file-text-bold',
            href: paths.dashboard.ekonomi.bokforing.bokslut.root,
        });
    }

    // ----- 8 domain cards -----

    const cardWidgets: DashboardWidget[] = [
        {
            id: 'card-inkorg',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <InkorgCard email={email} loading={emailLoading} />
            ),
        },
        {
            id: 'card-bank',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <BankCard
                    konton={bankkonton}
                    kontonLoading={bankkontonLoading}
                    transaktioner={banktransaktioner}
                    transaktionerLoading={banktransaktionerLoading}
                />
            ),
        },
        {
            id: 'card-lon',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <LönCard matchningar={löneMatchningar} loading={lönerLoading} />
            ),
        },
        {
            id: 'card-kundfaktura',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <KundfakturaCard
                    fakturor={kundfakturor}
                    loading={kundfakturorLoading}
                    detaljer={kundfakturaDetaljer}
                />
            ),
        },
        {
            id: 'card-inkop',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <InköpCard respons={inköpRespons} loading={inköpLoading} />
            ),
        },
        {
            id: 'card-skatter',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <SkatterCard respons={skattekontoutdrag} loading={skattekontoutdragLoading} />
            ),
        },
        {
            id: 'card-moms',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <MomsCard
                    organisation={organisation}
                    deklarationer={momsdeklarationer}
                    loading={momsLoading}
                />
            ),
        },
        {
            id: 'card-bokslut',
            size: { xs: 12, sm: 6, lg: 4 },
            component: (
                <BokslutCard
                    process={bokslutProcess}
                    loading={bokslutLoading}
                    rakenskapsar={härlettRäkenskapsår}
                    förslag={aktieutdelningFörslag}
                    beslutadAktieutdelning={beslutadAktieutdelning}
                />
            ),
        },
    ];

    const widgets: DashboardWidget[] = [
        { id: 'welcome', size: { xs: 12 }, component: welcome },
        ...(pills.length > 0
            ? [{ id: 'summary-pills', size: { xs: 12 }, component: <SummaryPills pills={pills} /> }]
            : []),
        ...cardWidgets,
    ];

    return (
        <DashboardContent maxWidth="xl">
            <DynamicDashboard widgets={widgets} />
        </DashboardContent>
    );
}

// ----------------------------------------------------------------------
// Data-bound card components. Kept in this file because they only make
// sense as parts of the aktiebolag dashboard.
// ----------------------------------------------------------------------

function InkorgCard({ email, loading }: { email: InkommandeEmail[]; loading: boolean }) {
    const senaste = [...email]
        .sort((a, b) => {
            const av = dayjs(a.mottagenDatum).valueOf();
            const bv = dayjs(b.mottagenDatum).valueOf();
            return bv - av;
        })
        .slice(0, 3);

    const antal = email.length;

    if (loading && antal === 0) {
        return (
            <DomainCard
                title="Inkorg"
                icon="solar:inbox-bold"
                theme={NyttFärgTema.Kombinationer[0]}
                href={paths.dashboard.inkorg}
                primaryMetric={{ value: '—', label: 'mail' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (antal === 0) {
        return (
            <DomainCard
                title="Inkorg"
                icon="solar:inbox-bold"
                theme={NyttFärgTema.Kombinationer[0]}
                href={paths.dashboard.inkorg}
                emptyMessage="Inga mottagna mail ännu. Anslut din email för att börja ta emot."
            />
        );
    }

    return (
        <DomainCard
            title="Inkorg"
            icon="solar:inbox-bold"
            theme={NyttFärgTema.Kombinationer[0]}
            href={paths.dashboard.inkorg}
            primaryMetric={{ value: antal, label: antal === 1 ? 'mail' : 'mail' }}
            statusChip={{ label: 'ANSLUTEN', color: ChipFärger[0] }}
        >
            <MiniList>
                {senaste.map((m) => (
                    <MiniRow
                        key={m.id}
                        primary={m.avsandare || 'Okänd avsandare'}
                        secondary={m.amne || '(Inget ämne)'}
                        meta={formatRelativeReceived(m.mottagenDatum)}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}

function BankCard({
    konton,
    kontonLoading,
    transaktioner,
    transaktionerLoading,
}: {
    konton: IBankkonto[];
    kontonLoading: boolean;
    transaktioner: IBanktransaktion[];
    transaktionerLoading: boolean;
}) {
    const totalSaldo = konton.reduce((sum, k) => sum + (k.saldo ?? 0), 0);
    const kontonAntal = konton.length;

    const senasteSync = konton
        .map((k) => k.senastSynkroniseradSaldo ?? k.senastSynkroniserad)
        .filter((t): t is string => Boolean(t))
        .sort()
        .at(-1);

    const senasteTransaktioner = [...transaktioner]
        .sort((a, b) => {
            const av = dayjs(a.valutaDatum as unknown as string).valueOf();
            const bv = dayjs(b.valutaDatum as unknown as string).valueOf();
            return bv - av;
        })
        .slice(0, 3);

    const loading = kontonLoading || (transaktionerLoading && transaktioner.length === 0);

    if (loading) {
        return (
            <DomainCard
                title="Bank & sparkonton"
                icon="solar:card-bold"
                theme={NyttFärgTema.Kombinationer[5]}
                href={paths.dashboard.ekonomi.bank.root}
                primaryMetric={{ value: '—', label: 'totalt saldo' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (kontonAntal === 0) {
        return (
            <DomainCard
                title="Bank & sparkonton"
                icon="solar:card-bold"
                theme={NyttFärgTema.Kombinationer[5]}
                href={paths.dashboard.ekonomi.bank.root}
                statusChip={{ label: 'EJ ANSLUTEN', color: ChipFärger[8] }}
                emptyMessage="Anslut ett bankkonto för att se saldon och transaktioner här."
            />
        );
    }

    return (
        <DomainCard
            title="Bank & sparkonton"
            icon="solar:card-bold"
            theme={NyttFärgTema.Kombinationer[5]}
            href={paths.dashboard.ekonomi.bank.root}
            primaryMetric={{ value: formatKr(totalSaldo), label: 'totalt saldo' }}
            secondaryMetric={{
                value: `${kontonAntal}`,
                label: kontonAntal === 1 ? 'konto' : 'konton',
            }}
            statusChip={
                // Visa senaste synk-datum om det finns, annars ingen chip alls.
                senasteSync
                    ? { label: `SYNK ${formatRelativeSync(senasteSync).toUpperCase()}`, color: ChipFärger[5] }
                    : undefined
            }
        >
            {senasteTransaktioner.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Inga transaktioner ännu.
                </Typography>
            ) : (
                <MiniList>
                    {senasteTransaktioner.map((t) => {
                        const datum = dayjs(t.valutaDatum as unknown as string);
                        return (
                            <MiniRow
                                key={t.id}
                                primary={t.referens || t.typ || 'Transaktion'}
                                meta={datum.isValid() ? datum.format('D MMM') : undefined}
                                trailing={formatSignedKr(t.belopp)}
                                trailingColor={t.belopp > 0 ? 'success.main' : 'text.primary'}
                            />
                        );
                    })}
                </MiniList>
            )}
        </DomainCard>
    );
}

function LönCard({ matchningar, loading }: { matchningar: IMatchningAvLön[]; loading: boolean }) {
    const theme = NyttFärgTema.Kombinationer[2];
    const href = paths.dashboard.ekonomi.loner.root;

    if (loading && matchningar.length === 0) {
        return (
            <DomainCard title="Lön" icon="solar:wallet-money-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'utbetalt i år' }}>
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (matchningar.length === 0) {
        return (
            <DomainCard title="Lön" icon="solar:wallet-money-bold" theme={theme} href={href}
                emptyMessage="Inga lönehändelser för räkenskapsåret ännu." />
        );
    }

    const utbetalt = (m: IMatchningAvLön) =>
        Math.abs(m.valdaBanktransaktioner?.reduce((s, t) => s + (t.belopp ?? 0), 0) ?? 0);
    const totalUtbetalt = matchningar.reduce((s, m) => s + utbetalt(m), 0);
    const bokförda = matchningar.filter((m) => m.bokförd).length;
    const antal = matchningar.length;

    const lönSorteringsvärde = (m: IMatchningAvLön) => {
        const start = m.arbetsgivardeklaration?.period?.förstaDag;
        const d = start ? dayjs(start as any) : null;
        return d && d.isValid()
            ? d.valueOf()
            : Number(String(m.arbetsgivardeklaration?.redovisningsperiod ?? '').replace(/\D/g, '')) || 0;
    };
    const senaste = [...matchningar]
        .sort((a, b) => lönSorteringsvärde(b) - lönSorteringsvärde(a))
        .slice(0, 3);

    return (
        <DomainCard
            title="Lön"
            icon="solar:wallet-money-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: formatKr(totalUtbetalt), label: 'utbetalt i år' }}
            secondaryMetric={{ value: `${antal}`, label: antal === 1 ? 'lönekörning' : 'lönekörningar' }}
            statusChip={{ label: `${bokförda}/${antal} BOKFÖRDA`, color: ChipFärger[2] }}
        >
            <MiniList>
                {senaste.map((m) => (
                    <MiniRow
                        key={m.id}
                        primary={m.arbetsgivardeklaration?.redovisningsperiod || 'Lönekörning'}
                        secondary={m.bokförd ? 'Bokförd' : 'Ej bokförd'}
                        trailing={formatSignedKr(-utbetalt(m))}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}

function KundfakturaCard({
    fakturor,
    loading,
    detaljer,
}: {
    fakturor: IKundfakturaFörslag[];
    loading: boolean;
    detaljer: Record<string, IKundfakturaDetalj>;
}) {
    const theme = NyttFärgTema.Kombinationer[10];
    const href = paths.dashboard.ekonomi.fakturor.kundfakturor;

    if (loading && fakturor.length === 0) {
        return (
            <DomainCard title="Kundfaktura" icon="solar:bill-list-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'kundfakturor' }}>
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (fakturor.length === 0) {
        return (
            <DomainCard title="Kundfaktura" icon="solar:bill-list-bold" theme={theme} href={href}
                emptyMessage="Inga uppladdade kundfakturor ännu." />
        );
    }

    const antal = fakturor.length;
    const betalda = fakturor.filter((f) => f.matchadBetalning).length;
    const obetalda = antal - betalda;

    const senaste = [...fakturor]
        .sort((a, b) => dayjs(b.uppladdadVid).valueOf() - dayjs(a.uppladdadVid).valueOf())
        .slice(0, 3);

    return (
        <DomainCard
            title="Kundfaktura"
            icon="solar:bill-list-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: antal, label: antal === 1 ? 'kundfaktura' : 'kundfakturor' }}
            secondaryMetric={
                obetalda > 0
                    ? { value: `${obetalda}`, label: obetalda === 1 ? 'obetald' : 'obetalda' }
                    : { value: `${betalda}`, label: 'betalda' }
            }
            statusChip={
                obetalda === 0
                    ? { label: 'ALLA BETALDA', color: ChipFärger[5] }
                    : { label: `${obetalda} OBETALDA`, color: ChipFärger[10] }
            }
        >
            <MiniList>
                {senaste.map((f) => {
                    const detalj = f.affärshändelseId ? detaljer[f.affärshändelseId] : undefined;
                    const period = detalj ? formatÅrMånad(detalj.period) : undefined;
                    // Belopp exkl. moms är huvudbeloppet; momsdelen visas som undertext.
                    const exkl = detalj?.exklMoms;
                    return (
                        <MiniRow
                            key={f.id}
                            primary={period || detalj?.kund || f.filnamn || 'Kundfaktura'}
                            secondary={
                                detalj ? `Moms ${formatKr(detalj.moms)}` : f.matchadBetalning ? 'Betald' : 'Obetald'
                            }
                            trailing={
                                exkl != null
                                    ? formatSignedKr(exkl)
                                    : f.banktransaktionBelopp != null
                                      ? formatSignedKr(f.banktransaktionBelopp)
                                      : undefined
                            }
                            trailingColor="success.main"
                        />
                    );
                })}
            </MiniList>
        </DomainCard>
    );
}

function InköpCard({ respons, loading }: { respons: InköpParsningResponse | undefined; loading: boolean }) {
    const theme = NyttFärgTema.Kombinationer[8];
    const href = paths.dashboard.ekonomi.fakturor.levfakturor;

    if (loading && !respons) {
        return (
            <DomainCard title="Inköp & utgifter" icon="solar:cart-3-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'inköp' }}>
                <Skeleton variant="text" height={20} />
            </DomainCard>
        );
    }

    const inköp = respons?.inköp ?? [];
    const omatchade = respons?.omatchadeInköpsUnderlag ?? [];
    const matchningar = respons?.inköpsMatchningar ?? [];

    if (inköp.length === 0 && omatchade.length === 0) {
        return (
            <DomainCard title="Inköp & utgifter" icon="solar:cart-3-bold" theme={theme} href={href}
                emptyMessage="Inga leverantörsfakturor eller utgifter ännu." />
        );
    }

    const inköpDatum = (i: IInköp) => {
        const d = i.affärshändelse?.bokföringsDatum ?? i.affärshändelse?.inträffade ?? i.banktransaktion?.valutaDatum;
        const parsed = d ? dayjs(d as any) : null;
        return parsed && parsed.isValid() ? parsed : null;
    };
    const inköpBelopp = (i: IInköp) => {
        if (i.banktransaktion?.belopp != null) return i.banktransaktion.belopp;
        const t = i.affärshändelse?.totaltBelopp;
        return t != null ? -Math.abs(t) : undefined;
    };

    // De 5 senaste inköpen, nyast först.
    const senaste = [...inköp]
        .sort((a, b) => (inköpDatum(b)?.valueOf() ?? 0) - (inköpDatum(a)?.valueOf() ?? 0))
        .slice(0, 5);

    return (
        <DomainCard
            title="Inköp & utgifter"
            icon="solar:cart-3-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: inköp.length, label: inköp.length === 1 ? 'inköp' : 'inköp' }}
            secondaryMetric={{ value: `${matchningar.length}`, label: 'matchade' }}
            statusChip={
                omatchade.length > 0
                    ? { label: `${omatchade.length} OMATCHADE`, color: ChipFärger[8] }
                    : { label: 'ALLT MATCHAT', color: ChipFärger[5] }
            }
        >
            {senaste.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    {omatchade.length} underlag väntar på matchning.
                </Typography>
            ) : (
                <MiniList>
                    {senaste.map((i) => {
                        const datum = inköpDatum(i);
                        const belopp = inköpBelopp(i);
                        return (
                            <MiniRow
                                key={i.id}
                                primary={
                                    i.affärshändelse?.beskrivning ||
                                    i.banktransaktion?.referens ||
                                    'Inköp'
                                }
                                secondary={datum ? datum.format('D MMM YYYY') : undefined}
                                trailing={belopp != null ? formatSignedKr(belopp) : undefined}
                            />
                        );
                    })}
                </MiniList>
            )}
        </DomainCard>
    );
}

function SkatterCard({ respons, loading }: { respons: SkattekontoutdragRespons | undefined; loading: boolean }) {
    const theme = NyttFärgTema.Kombinationer[11];
    const href = paths.dashboard.ekonomi.skatter.root;

    if (loading && !respons) {
        return (
            <DomainCard title="Skatter" icon="solar:scale-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'saldo' }}>
                <Skeleton variant="text" height={20} />
            </DomainCard>
        );
    }

    // De inlästa skattekontotransaktionerna exponeras i parsning.transaktioner. Faller tillbaka på
    // bokföringsposternas underliggande transaktioner om parsning saknas.
    const rawTransaktioner = respons?.parsning?.transaktioner ?? [];
    const bokföringsTransaktioner = (respons?.skattekontoutdrag?.bokföringAvSkatteverketTransaktioner ?? [])
        .map((b) => b.skatteverkettransaktion)
        .filter(Boolean);
    const transaktioner = rawTransaktioner.length > 0 ? rawTransaktioner : bokföringsTransaktioner;

    // Ta bort exakta dubbletter (upprepade uppladdningar skapar identiska rådata-rader).
    const unika = Array.from(
        new Map(
            transaktioner.map((t) => [
                t?.rådata ?? `${t?.datum}|${t?.beskrivning}|${t?.belopp}|${t?.id}`,
                t,
            ])
        ).values()
    );

    if (unika.length === 0) {
        return (
            <DomainCard title="Skatter" icon="solar:scale-bold" theme={theme} href={href}
                emptyMessage="Ladda upp ett skattekontoutdrag för att se översikt." />
        );
    }

    const senaste = [...unika]
        .sort((a, b) => (toDayjsSafe(b?.datum)?.valueOf() ?? 0) - (toDayjsSafe(a?.datum)?.valueOf() ?? 0))
        .slice(0, 3);

    // Saldo skattekonto = det löpande saldot (sista kolumnen i rådata) för den senaste
    // transaktionen — dvs. utgående saldo. Faller tillbaka på summan om rådata saknas.
    const utgåendeSaldo = parseSkattekontoSaldo(senaste[0]?.rådata);
    const saldo = utgåendeSaldo ?? unika.reduce((s, t) => s + (t?.belopp ?? 0), 0);

    return (
        <DomainCard
            title="Skatter"
            icon="solar:scale-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: formatKr(saldo), label: 'saldo skattekonto' }}
            secondaryMetric={{ value: `${unika.length}`, label: 'transaktioner' }}
        >
            <MiniList>
                {senaste.map((t, i) => {
                    const datum = toDayjsSafe(t?.datum);
                    return (
                        <MiniRow
                            key={t?.id ?? i}
                            primary={t?.beskrivning || t?.typ || 'Skattetransaktion'}
                            secondary={datum ? datum.format('D MMM YYYY') : undefined}
                            trailing={t?.belopp != null ? formatSignedKr(t.belopp) : undefined}
                            trailingColor={(t?.belopp ?? 0) >= 0 ? 'success.main' : 'text.primary'}
                        />
                    );
                })}
            </MiniList>
        </DomainCard>
    );
}

function MomsCard({
    organisation,
    deklarationer,
    loading,
}: {
    organisation: IOrganisation | undefined;
    deklarationer: IMomsdeklaration[];
    loading: boolean;
}) {
    const theme = NyttFärgTema.Kombinationer[9];
    const href = paths.dashboard.ekonomi.moms.root;
    const period = organisation?.momsDeklarationsPeriod;

    if (loading && deklarationer.length === 0) {
        return (
            <DomainCard title="Moms" icon="solar:document-text-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'deklarationer' }}>
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    // No declarations yet — fall back to the configured declaration period.
    if (deklarationer.length === 0) {
        if (!period) {
            return (
                <DomainCard title="Moms" icon="solar:document-text-bold" theme={theme} href={href}
                    emptyMessage="Inga momsdeklarationer ännu." />
            );
        }
        const periodLabel =
            period === 'MÅNAD' ? 'Månadsvis' : period === 'KVARTAL' ? 'Kvartalsvis' : 'Årligen';
        return (
            <DomainCard title="Moms" icon="solar:document-text-bold" theme={theme} href={href}
                primaryMetric={{ value: periodLabel, label: 'deklarationsperiod' }}
                statusChip={{ label: period, color: ChipFärger[9] }}
                emptyMessage="Inga momsdeklarationer registrerade ännu." />
        );
    }

    const periodSorteringsvärde = (d: IMomsdeklaration) => d.period?.sistaDag ?? d.sistaDagIPerioden ?? '';
    const sorterade = [...deklarationer].sort((a, b) =>
        String(periodSorteringsvärde(b)).localeCompare(String(periodSorteringsvärde(a)))
    );
    const senaste = sorterade[0];
    const inlämnade = deklarationer.filter((d) => d.inlämnadPå).length;

    return (
        <DomainCard
            title="Moms"
            icon="solar:document-text-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: formatMomsperiod(senaste?.period), label: 'senaste period' }}
            secondaryMetric={{ value: `${inlämnade}/${deklarationer.length}`, label: 'inlämnade' }}
            statusChip={
                senaste?.inlämnadPå
                    ? { label: 'INLÄMNAD', color: ChipFärger[5] }
                    : { label: 'EJ INLÄMNAD', color: ChipFärger[9] }
            }
        >
            <MiniList>
                {sorterade.slice(0, 3).map((d, i) => (
                    <MiniRow
                        key={d.id ?? i}
                        primary={formatMomsperiod(d.period)}
                        secondary={d.inlämnadPå ? 'Inlämnad' : 'Ej inlämnad'}
                        meta={d.inlämnadPå ? dayjs(d.inlämnadPå).format('D MMM YYYY') : undefined}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}

function BokslutCard({
    process,
    loading,
    rakenskapsar,
    förslag,
    beslutadAktieutdelning,
}: {
    process: IBokslutProcess | undefined;
    loading: boolean;
    rakenskapsar: IRäkenskapsår | undefined;
    förslag: AktieutdelningFörslagDto | null;
    beslutadAktieutdelning: number | undefined;
}) {
    // Härled totalt antal steg från processens stegStatus när det finns, annars fall tillbaka på 6.
    const stegVärden = process?.stegStatus ? Object.values(process.stegStatus) : [];
    const totaltSteg = stegVärden.length > 0 ? stegVärden.length : 6;
    const klaraSteg = stegVärden.filter((s) => s === 'KLAR').length;

    const årLabel = (() => {
        if (!rakenskapsar) return undefined;
        const start = rakenskapsar.förstaDag as any;
        const startDate = start?.format ? start.format('YYYY-MM-DD') : String(start);
        return startDate?.substring(0, 4);
    })();

    if (loading) {
        return (
            <DomainCard
                title="Bokslut"
                icon="solar:file-text-bold"
                theme={NyttFärgTema.Kombinationer[7]}
                href={paths.dashboard.ekonomi.bokforing.bokslut.root}
                primaryMetric={{ value: '—', label: 'steg klara' }}
            >
                <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 999 }} />
            </DomainCard>
        );
    }

    if (!process) {
        return (
            <DomainCard
                title="Bokslut"
                icon="solar:file-text-bold"
                theme={NyttFärgTema.Kombinationer[7]}
                href={paths.dashboard.ekonomi.bokforing.bokslut.root}
                emptyMessage="Inget räkenskapsår registrerat för bokslutsprocessen ännu."
            />
        );
    }

    const status = process.slutförd ? 'SLUTFÖRT' : klaraSteg > 0 ? 'PÅGÅR' : 'EJ PÅBÖRJAT';

    return (
        <DomainCard
            title="Bokslut"
            icon="solar:document-bold"
            theme={NyttFärgTema.Kombinationer[7]}
            href={paths.dashboard.ekonomi.bokforing.bokslut.root}
            primaryMetric={{ value: `${klaraSteg} / ${totaltSteg}`, label: 'steg klara' }}
            secondaryMetric={årLabel ? { value: årLabel, label: 'räkenskapsår' } : undefined}
            statusChip={{ label: status, color: ChipFärger[7] }}
        >
            {förslag ? (
                <Stack
                    divider={<Box sx={(theme) => ({ borderTop: `1px dashed ${theme.palette.divider}` })} />}
                    spacing={0}
                >
                    <NyckeltalRad etikett="Årets resultat" belopp={formatSignedKr(förslag.åretsResultatFöreSkatt)} />
                    <NyckeltalRad etikett="Skatt" belopp={formatSignedKr(-Math.abs(förslag.bolagsskatt))} />
                    <NyckeltalRad
                        etikett="Årets resultat efter skatt"
                        belopp={formatSignedKr(förslag.åretsResultatEfterSkatt)}
                        framhävd
                    />
                    <NyckeltalRad
                        etikett="Aktieutdelning"
                        belopp={formatKr(beslutadAktieutdelning ?? förslag.föreslagenAktieutdelning)}
                    />
                </Stack>
            ) : (
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            )}
        </DomainCard>
    );
}

function NyckeltalRad({
    etikett,
    belopp,
    framhävd,
}: {
    etikett: string;
    belopp: string;
    framhävd?: boolean;
}) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.55 }}>
            <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: '0.82rem', fontWeight: framhävd ? 700 : 500 }}
            >
                {etikett}
            </Typography>
            <Typography
                sx={{
                    fontWeight: framhävd ? 800 : 600,
                    fontSize: framhävd ? '0.9rem' : '0.82rem',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                }}
            >
                {belopp}
            </Typography>
        </Stack>
    );
}

// ----------------------------------------------------------------------
// Shared mini-body primitives used inside cards.
// ----------------------------------------------------------------------

function MiniList({ children }: { children: React.ReactNode }) {
    return (
        <Stack
            divider={
                <Box sx={(theme) => ({ borderTop: `1px dashed ${theme.palette.divider}` })} />
            }
            spacing={0}
        >
            {children}
        </Stack>
    );
}

function MiniRow({
    primary,
    secondary,
    meta,
    trailing,
    trailingColor,
}: {
    primary: string;
    secondary?: string;
    meta?: string;
    trailing?: string;
    trailingColor?: string;
}) {
    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 0.85, minHeight: 36 }}
        >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.82rem' }}
                >
                    {primary}
                </Typography>
                {secondary && (
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
                    >
                        {secondary}
                    </Typography>
                )}
            </Box>
            {trailing ? (
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: trailingColor ?? 'text.primary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                    {trailing}
                </Typography>
            ) : meta ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                >
                    {meta}
                </Typography>
            ) : null}
        </Stack>
    );
}
